// BFSG
import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import ProductImage from 'theme/atoms/ProductImage'
import whitePixel from 'utils/whitePixel'
import Price from 'theme/molecules/Price'
import Button from 'theme/atoms/Button'
import { ReactComponent as ArrowButtonRight } from 'assets/arrow-button-right.svg'
import Brand from './Brand'
import Flags from 'theme/molecules/Flags'
import useProduct, { Product } from './useProduct'
import Markdown from 'theme/atoms/Markdown'
import getProductLink from 'utils/getProductLink'
import Rating from 'theme/molecules/Rating'

export type Props = {
  sku: string
  brandPath: string
  title?: React.ReactNode
  btnLabel: string
  product?: Product
  'data-cy-state'?: string
}

export default function ProductHighlight(props: Props) {
  const { fetching, data: productData } = useProduct(
    !props.product ? props.sku : ''
  )
  const product = props.product ?? productData

  const descriptionRef = React.useRef<HTMLDivElement>(null)
  const [descOverflow, setDescOverflow] = React.useState(false)

  React.useEffect(() => {
    if (descriptionRef.current) {
      const el = descriptionRef.current
      setDescOverflow(el.scrollHeight > el.clientHeight)
    }
  }, [product?.description])

  if (!fetching && !product) return null

  const pdpLink = product ? getProductLink(product) : ''

  return (
    <Wrapper
      className="ProductHighlight"
      data-cy-ctx="molecules/HighlightProduct"
      data-cy-state={props['data-cy-state']}
    >
      {props.title && (
        <h2 data-cy-state="show-title" className="title">
          {props.title}
        </h2>
      )}
      <div className="content">
        <div className="content-inner">
          <div className="image">
            {product && (
              <span data-cy-state="render:flags">
                <Flags product={product} type={'widget'} />
              </span>
            )}
            {product && (
              <ProductImage
                data-cy-state="render:show-image"
                alt={product.title}
                img={product.images.imageWeb[0]}
                size="s"
              />
            )}
            {!product && (
              <ProductImage
                data-cy-state="placeholder:show-image"
                img={{
                  url: whitePixel,
                  classes: ['ASSET_FS']
                }}
                size="s"
                alt=""
              />
            )}
          </div>
          <div className="info">
            {product && (
              <Brand
                data-cy-state="render:brand"
                brandPath={props.brandPath}
                product={product}
              />
            )}
            <div className="title">{product?.title ?? ' '}</div>
            <Rating
              containerID={product?.containerID ?? ''}
              ratingAverage={product?.productReviews.productReviewAvg}
              ratingCount={product?.productReviews.productReviewCount}
            />
            {product && (
              <>
                <Price
                  data-cy-state="render:show-product-price"
                  prices={product.prices}
                  logic="piece"
                  unit={product.unit}
                  options={{
                    hideEcoTax: true,
                    amount: 1,
                    pageType: 'Other',
                    showSubline: product.unit.purchaseUnit > 1,
                    specialType: 'HighlightProduct'
                  }}
                />
              </>
            )}
            
            <div
              ref={descriptionRef}
              className={`description${descOverflow ? ' has-overflow' : ''}`}
            >
              <Markdown html={product?.description ?? ' '} />
            </div>
            <div className="buttons-desktop buttons">
              <Button
                variation="secondary"
                to={pdpLink}
                $fullWidth
                data-cy-handle="product-link"
              >
                {props.btnLabel} <ArrowButtonRight />
              </Button>
            </div>
          </div>
          {/* Mobile buttons below image */}
          <div className="buttons-mobile buttons">
            <Button
              variation="secondary"
              to={pdpLink}
              $fullWidth
              data-cy-handle="product-link"
            >
              {props.btnLabel} <ArrowButtonRight />
            </Button>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background: ${theme.colors.shade.primaryUltraBright};

  > .title {
    ${theme.ty('rc-2xl', null, 'rc-3xl')};
    padding-top: ${theme.spacing('ml')};
    margin-bottom: ${theme.spacing('m')};
    margin-left: ${theme.spacing('l')};
    color: ${theme.colors.b0};
  }

  > .content {
    > .content-inner {
      margin-left: ${theme.spacing('l')};
      margin-right: ${theme.spacing('l')};
      display: grid;
      grid-template:
        'image info'
        'buttons buttons'
        / 2fr 3fr;
      grid-column-gap: ${theme.spacing('xs')};
      grid-row-gap: ${theme.spacing('m')};

      @media (min-width: ${theme.ms.MD}px) {
        grid-template:
          'image info'
          'image buttons'
          'image buttons'
          '. .'
          / auto 1fr;
        grid-column-gap: ${theme.spacing('m')};
      }
    }

    > .content-inner > .image {
      grid-area: image;
      background: ${theme.colors.white};
      padding: ${theme.spacing('xxs')};
      position: relative;
    }

    > .content-inner > .info {
      grid-area: info;

      @media (min-width: ${theme.ms.MD}px) {
        margin-bottom: ${theme.spacing('xs')};
      }

      > .title {
        margin-bottom: ${theme.spacing('xs')};
        ${theme.ty('rc-base', null, 'rc-2xl')};
      }
      > .Rating {
        margin-bottom: ${theme.spacing('xs')};
      }

      > .Price {
        ${theme.ty('r-s')}; /* Kleinere Schriftgröße für Packpreis */
      }

      > .description {
        display: none;
        overflow: hidden;
        position: relative;
        margin-top: ${theme.spacing('xs')};
        
        @media (min-width: ${theme.ms.MD}px) {
          /* Desktop: Beschreibung anzeigen aber auf 3 Zeilen clampen */
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-clamp: 3; /* (experimentell) */
          margin-bottom: ${theme.spacing('m')}; /* Fester Abstand zu Buttons */
        }

        p:first-child {
          margin-top: 0 !important;
        }

        &:after {
          content: '';
          position: absolute;
          bottom: 0;
          height: 60px;
          left: 0;
          right: 0;
        }
      }
    }

    > .content-inner > .info > .buttons-desktop {
      display: none;
      > .Button {
        width: auto;
      }
      @media (min-width: ${theme.ms.MD}px) {
        display: flex;
      }
    }

    > .content-inner > .buttons-mobile {
      display: flex;
      margin-top: ${theme.spacing('s')};
      margin-bottom: ${theme.spacing('l')};
      grid-area: buttons;
      
      @media (min-width: ${theme.ms.MD}px) {
        display: none;
        
      }
    }
    .buttons {
      width: 100%;
      display: flex;
      
      margin-bottom: ${theme.spacing('l')};
    }
  }
`
